import { useCallback, useEffect, useRef, useState } from 'react';
import { animate, useMotionValue, useReducedMotion } from 'framer-motion';
import type { PanInfo } from 'framer-motion';

const AUTOPLAY_MS = 4500;
const RESUME_AFTER_MS = 9000;
const GAP = 16;
const EASE: [number, number, number, number] = [0.23, 1, 0.32, 1];

function slideWidthFor(containerWidth: number) {
  const ratio = containerWidth < 640 ? 0.6 : containerWidth < 1024 ? 0.38 : 0.26;
  return Math.min(containerWidth * ratio, 290);
}

export function useModuleCarousel(count: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [pausedUntil, setPausedUntil] = useState(0);
  const dragged = useRef(false);
  const x = useMotionValue(0);
  const reduceMotion = useReducedMotion();

  const slideWidth = slideWidthFor(containerWidth);
  const targetX = containerWidth / 2 - (index * (slideWidth + GAP) + slideWidth / 2);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const snap = useCallback(
    (instant = false) => {
      animate(x, targetX, instant || reduceMotion ? { duration: 0 } : { duration: 0.42, ease: EASE });
    },
    [x, targetX, reduceMotion]
  );

  const firstLayout = useRef(true);
  useEffect(() => {
    if (!containerWidth) return;
    snap(firstLayout.current);
    firstLayout.current = false;
  }, [snap, containerWidth]);

  const wrap = useCallback((i: number) => (i + count) % count, [count]);

  const goTo = useCallback(
    (i: number, manual = true) => {
      setIndex(wrap(i));
      if (manual) setPausedUntil(Date.now() + RESUME_AFTER_MS);
    },
    [wrap]
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Autoplay: advances every few seconds unless hovered, recently touched, or reduced motion.
  useEffect(() => {
    if (reduceMotion || hovered) return;
    const wait = Math.max(AUTOPLAY_MS, pausedUntil - Date.now());
    const t = window.setTimeout(() => setIndex((i) => wrap(i + 1)), wait);
    return () => window.clearTimeout(t);
  }, [index, hovered, pausedUntil, reduceMotion, wrap]);

  const onDragStart = () => {
    dragged.current = true;
  };
  const onDragEnd = (_: unknown, info: PanInfo) => {
    const threshold = slideWidth * 0.2;
    if (info.offset.x < -threshold || info.velocity.x < -400) goTo(index + 1);
    else if (info.offset.x > threshold || info.velocity.x > 400) goTo(index - 1);
    else snap();
    window.setTimeout(() => {
      dragged.current = false;
    }, 0);
  };

  const onSlideClick = (i: number) => {
    if (dragged.current || i === index) return;
    goTo(i);
  };

  return {
    containerRef,
    x,
    index,
    slideWidth,
    gap: GAP,
    ready: containerWidth > 0,
    goTo,
    next,
    prev,
    onDragStart,
    onDragEnd,
    onSlideClick,
    setHovered,
    reduceMotion,
  };
}
